import { createContext, useContext, useState, useCallback } from 'react';
import { debugLog } from '../utils/logger';

const SurveyContext = createContext(null);

/**
 * SurveyProvider
 * Holds all state shared between ValidatePage and PreferencesPage.
 * sessionToken and patientData are persisted to sessionStorage so a
 * page refresh doesn't lose state mid-flow.
 */
export function SurveyProvider({ children }) {
    const [sessionToken, setSessionTokenState] = useState(
        () => sessionStorage.getItem('surveySessionToken') || null
    );

    const [patientData, setPatientDataState] = useState(() => {
        const stored = sessionStorage.getItem('surveyPatient');
        return stored ? JSON.parse(stored) : null;
    });

    const [practiceName, setPracticeNameState] = useState(
        () => sessionStorage.getItem('surveyPracticeName') || null
    );

    const setSessionToken = useCallback((token) => {
        debugLog('ui_session_token_updated', { hasToken: Boolean(token) });
        setSessionTokenState(token);
        if (token) {
            sessionStorage.setItem('surveySessionToken', token);
        } else {
            sessionStorage.removeItem('surveySessionToken');
        }
    }, []);

    const setPatientData = useCallback((data) => {
        debugLog('ui_patient_data_updated', {
            hasData: Boolean(data),
            fieldCount: data ? Object.keys(data).length : 0
        });
        setPatientDataState(data);
        if (data) {
            sessionStorage.setItem('surveyPatient', JSON.stringify(data));
        } else {
            sessionStorage.removeItem('surveyPatient');
        }
    }, []);

    const setPracticeName = useCallback((name) => {
        debugLog('ui_practice_name_updated', { hasPracticeName: Boolean(name) });
        setPracticeNameState(name);
        if (name) {
            sessionStorage.setItem('surveyPracticeName', name);
        } else {
            sessionStorage.removeItem('surveyPracticeName');
        }
    }, []);

    const clearSurveyState = useCallback(() => {
        debugLog('ui_survey_state_cleared');
        setSessionToken(null);
        setPatientData(null);
        setPracticeName(null);
    }, [setSessionToken, setPatientData, setPracticeName]);

    return (
        <SurveyContext.Provider value={{
            sessionToken,
            setSessionToken,
            patientData,
            setPatientData,
            practiceName,
            setPracticeName,
            clearSurveyState
        }}>
            {children}
        </SurveyContext.Provider>
    );
}

/**
 * useSurvey — convenience hook
 * Throws if used outside SurveyProvider.
 */
export function useSurvey() {
    const ctx = useContext(SurveyContext);
    if (!ctx) throw new Error('useSurvey must be used within a SurveyProvider');
    return ctx;
}
